import React, { useEffect, useState } from 'react';
import { CalculatorService } from '../services/CalculatorService';
import makeApiRoute from '../utils/apiRoute';
import Branding from './Branding';
import NumberInput from './NumberInput';
import MultiButton from './MultiButton';
import HalfNumberInput from './HalfNumberInput';
import Results from './Results';
import SubmitButton from './SubmitButton';
import TextInput from './TextInput';
import ToggleSlider from './ToggleSlider';
import { ConfigurationService } from '../services/ConfigurationService';
import GDPR from './GDPR';
import FontFaceObserver from 'fontfaceobserver'
import CalculatorHeader from './CalculatorHeader';

export interface IState {
   gender: string;
   age: string;
   system: string;
   centimeters: string;
   feet: string;
   inches: string;
   currentWeightKgs: string;
   goalWeightKgs: string;
   currentWeightLbs: string;
   goalWeightLbs: string;
   bfp: string;
   dailyActivity: string;
   diet: string;
   name: string;
   emailAddress: string;
   gdpr: boolean;
   marketing: boolean;
   [key: string]: string | boolean;
}

export interface IResponseState {
   protein: number;
   carbs: number;
   fat: number;
   calories: number;
}

interface ICalculatorState {
   branding: boolean;
}

interface IConfigurationState {
   headerText: string | null,
   gdprText: string | null,
   marketingText: string | null,
   requiredText: string | null,
   privacyUrl: URL | null,
   resultsText: string | null,
   resultsButtonText: string | null,
   resultsButtonUrl: URL | null
}

const Calculator: React.FC = () => {

   const [state, setState] = useState<IState>({
      gender: 'Male',
      age: '',
      system: 'Metric',
      centimeters: '',
      feet: '',
      inches: '',
      currentWeightKgs: '',
      goalWeightKgs: '',
      currentWeightLbs: '',
      goalWeightLbs: '',
      bfp: '',
      dailyActivity: 'Sedentary',
      diet: 'General',
      name: '',
      emailAddress: '',
      gdpr: false,
      marketing: false
   });

   const [rState, setRState] = useState<IResponseState>({
      protein: 0,
      carbs: 0,
      fat: 0,
      calories: 0
   })

   const [cState, setCState] = useState<ICalculatorState>({
      branding: false
   })

   const [configState, setConfigState] = useState<IConfigurationState>({
      headerText: null,
      gdprText: null,
      marketingText: null,
      requiredText: null,
      privacyUrl: null,
      resultsText: null,
      resultsButtonText: null,
      resultsButtonUrl: null
   })

   const [loaded, setLoaded] = useState(false)

   function findGetParameter(parameterName: string) {
      var result = null,
         tmp = [];
      window.location.search
         .substring(1)
         .split("&")
         .forEach(function (item) {
            tmp = item.split("=");
            if (tmp[0] === parameterName) result = decodeURIComponent(tmp[1]);
         });
      return result;
   }

   function loadStyle(href: string) {
      // avoid duplicates
      for (var i = 0; i < document.styleSheets.length; i++) {
         if (document.styleSheets[i].href === href) {
            return;
         }
      }
      var head = document.getElementsByTagName('head')[0];
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      link.onload = checkB
      head.appendChild(link);
   }

   function checkB() {
      setTimeout(() => {

         const label = document.createElement('span')
         document.body.appendChild(label);
         const style = getComputedStyle(label);
         const fontStyle = style.fontFamily;
         const fontType = fontStyle.split(',')[0].replaceAll('"', '');
         var font = new FontFaceObserver(fontType);

         font.load().then(function () {
            setTimeout(() => {
               document.body.removeChild(label);
               resetIFrameHeight();
            }, 0)
         }, function () {
            document.body.removeChild(label);
            resetIFrameHeight();
         });
      }, 0)
      setTimeout(() => {
         const branding = document.createElement('div');
         branding.classList.add('calculator-branding');
         document.body.appendChild(branding);
         const style = getComputedStyle(branding);
         const display = style.display;
         setCState({
            branding: display.indexOf('block') > -1
         })
         document.body.removeChild(branding);
      }, 0)
      return null;
   }

   const resetIFrameHeight = () => {
      let height = document.getElementsByTagName("body")[0].scrollHeight;
      let message = { func: 'setIFrameHeight', value: height };
      window.parent.postMessage(JSON.stringify(message), '*');
   }

   const scrollTopIFrame = () => {
      let message = { func: 'scrollIFrameTop', value: 1 };
      window.parent.postMessage(JSON.stringify(message), '*');
   }

   useEffect(() => {
      let apiKey = findGetParameter('apiKey') || 'null'
      loadStyle(makeApiRoute(`/styles/${apiKey}/widget.css?t=${Date.now()}`))
      ConfigurationService.getConfiguration(apiKey)
         .then(response => {
            if (response.status === 201) {
               setLoaded(true);
               setConfigState({
                  ...response.data
               })
               setTimeout(() => {
                  resetIFrameHeight();
               }, 0)
            }
         })
   }, []);

   const updateInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
      let val = event.target.value
      if (event.target.name === 'emailAddress') {
         val = val.replace(' ', '')
         val = val.toLowerCase()
      }
      setState({
         ...state,
         [event.target.name]: val
      });
   };

   const updateToggle = (name: string, value: string) => {
      setState({ ...state, [name]: value })
      if (name === 'system') {
         setTimeout(() => {
            resetIFrameHeight();
         }, 0)
      }
   };

   const decreaseInput = (key: string, min: number, max: number): void => {
      let val = parseInt(state[key] as string) - 1;
      if (val && val < min) {
         val = min;
      }

      if (!val) {
         val = max;
      }

      setState({
         ...state,
         [key]: `${val}`
      })
   }

   const increaseInput = (key: string, min: number, max: number): void => {
      let val = parseInt(state[key] as string) + 1;
      if (val && val > max) {
         val = max;
      }

      if (!val) {
         val = min;
      }

      setState({
         ...state,
         [key]: `${val}`
      })
   }

   const submitCalcInfo = (event: React.MouseEvent<HTMLElement>, inputs: IState): void => {
      const data = {
         ...inputs
      }
      if (data.system === 'Metric') {
         data.currentWeightLbs = data.currentWeightKgs
         data.goalWeightLbs = data.goalWeightKgs
         data.feet = '5'
         data.inches = '0'
      } else {
         data.currentWeightKgs = data.currentWeightLbs
         data.goalWeightKgs = data.goalWeightLbs
         data.centimeters = '170'
      }
      data.gender = data.gender.slice(0, 1)
      const apiKey = findGetParameter('apiKey') || ''

      CalculatorService.submit(apiKey, data)
         .then(response => {
            const res = response.data;
            setRState({
               ...rState,
               protein: res.protein,
               fat: res.fat,
               carbs: res.carbs,
               calories: res.calories
            })
            setTimeout(() => {
               scrollTopIFrame();
               resetIFrameHeight();
            }, 100)
         })
   }

   const reset = () => {
      setRState({
         protein: 0,
         fat: 0,
         carbs: 0,
         calories: 0
      })
      setTimeout(() => {
         scrollTopIFrame();
         resetIFrameHeight();
      }, 0)
   }

   const resetRState = (event: React.MouseEvent<HTMLElement>): void => {
      if (configState.resultsButtonUrl?.href && configState.resultsButtonText) {
         window.open(configState.resultsButtonUrl.href, '_blank', 'noreferrer');
      } else {
         reset();
      }
   }

   const validCalcData = (inputs: IState) => {
      const data = {
         ...inputs
      }
      let valid = true
      if (data.system === 'Metric') {
         data.currentWeightLbs = data.currentWeightKgs
         data.goalWeightLbs = data.goalWeightKgs
         data.feet = '5'
         data.inches = '0'
      } else {
         data.currentWeightKgs = data.currentWeightLbs
         data.goalWeightKgs = data.goalWeightLbs
         data.centimeters = '170'
      }
      let key: keyof typeof data;
      for (key in data) {
         if (data[key] === '' || data[key] === false) {
            valid = false
         }
      }
      if (!data.emailAddress.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i)) {
         valid = false
      }
      return valid
   }

   const updateGdpr = () => {
      let gdpr = !state.gdpr
      setState({
         ...state,
         gdpr
      })
   }

   const updateMarketing = () => {
      let marketing = !state.marketing
      setState({
         ...state,
         marketing
      })
   }

   return (

      <div className='calculator-container'>
         {!loaded ? null :
            <>
               <CalculatorHeader text={rState.calories !== 0 || findGetParameter('r') === '1' ? 'YOUR RESULTS' : configState.headerText || ''} />
               {rState.calories === 0 && findGetParameter('r') !== '1' ?
                  <>
                     <ToggleSlider label="Gender" name="gender" options={["Male", "Female"]} value={state.gender} updateValue={updateToggle} />
                     <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Age" name="age" placeholder="age" value={state.age} min={18} max={75} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                     <div className='calculator-section-container-a border'>
                        <ToggleSlider label="Units" name="system" options={["Metric", "Imperial"]} optionLabels={["cm / kg", "ft in / lbs"]} value={state.system} updateValue={updateToggle} />
                        {state.system === 'Metric' ?
                           <>
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Height (cm)" name="centimeters" placeholder="cm" value={state.centimeters} min={120} max={210} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Current Weight (kg)" name="currentWeightKgs" placeholder="kg" value={state.currentWeightKgs} min={30} max={250} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Goal Weight (kg)" name="goalWeightKgs" placeholder="kg" value={state.goalWeightKgs} min={30} max={250} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                           </>
                           :
                           <>
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Height (ft)" name="feet" placeholder="ft" value={state.feet} min={4} max={6} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Height (in)" name="inches" placeholder="in" value={state.inches} min={0} max={11} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Current Weight (lbs)" name="currentWeightLbs" placeholder="lbs" value={state.currentWeightLbs} min={60} max={550} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                              <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Goal Weight (lbs)" name="goalWeightLbs" placeholder="lbs" value={state.goalWeightLbs} min={60} max={550} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                           </>
                        }
                     </div>
                     <div className='calculator-section-container-b border'>
                        <NumberInput increaseInput={increaseInput} decreaseInput={decreaseInput} label="Body Fat Percent" name="bfp" placeholder="%" value={state.bfp} min={6} max={50} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                        <ToggleSlider label="Diet Type" name="diet" options={["General", "Low Carb"]} value={state.diet} updateValue={(name: string, value: string) => { setState({ ...state, [name]: value }) }} />
                        <MultiButton label="Activity Level" name="dailyActivity" options={["Sedentary", "Lightly Active", "Moderately Active", "Very Active"]} value={state.dailyActivity} updateValue={updateToggle} />
                     </div>
                     <TextInput label="Name" name="name" placeholder={state.gender === 'Male' ? "John Doe" : "Jane Doe"} value={state.name} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                     <TextInput label="Email Address" name="emailAddress" placeholder={state.gender === 'Male' ? "john@example.org" : "jane@example.org"} value={state.emailAddress} updateInput={(e: React.ChangeEvent<HTMLInputElement>) => { updateInput(e) }} />
                     <GDPR gdprText={configState.gdprText || ''}
                        marketingText={configState.marketingText || ''}
                        requiredText={configState.requiredText || ''}
                        privacyUrl={configState.privacyUrl}
                        gdpr={state.gdpr}
                        marketing={state.marketing}
                        updateGdpr={updateGdpr}
                        updateMarketing={updateMarketing} />
                     <SubmitButton label="Submit" disabled={!validCalcData(state)} submit={(e: React.MouseEvent<HTMLElement>) => { submitCalcInfo(e, state) }} branding={cState.branding} resetIFrameHeight={resetIFrameHeight} />
                  </>
                  :
                  <>
                     <Results protein={rState.protein} carbs={rState.carbs} fat={rState.fat} calories={rState.calories} resultsText={configState.resultsText} reset={reset}></Results>
                     <SubmitButton label={configState.resultsButtonText || 'Back'} disabled={false} submit={(e: React.MouseEvent<HTMLElement>) => { resetRState(e) }} branding={cState.branding} resetIFrameHeight={resetIFrameHeight} />
                  </>
               }
            </>
         }
      </div>
   );
};

export default Calculator;
